import React, { useState } from 'react';
import api from '../services/api';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Typography,
    Box,
    Grid,
    Paper,
    Card,
    CardContent,
    Button,
    CircularProgress,
    Container,
    Chip,
    Fade,
    Grow,
    FormControlLabel,
    Switch
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    AccountBalanceWallet,
    Receipt,
    CalendarMonth,
    Assessment
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import TransactionGroup from './TransactionGroup';
import TransactionSearch from './TransactionSearch';

function Home() {
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [hiddenTransactions, setHiddenTransactions] = useState(new Set());
    const [expandedGroup, setExpandedGroup] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showAllTransactions, setShowAllTransactions] = useState(false);

    const handleSaveTransactionType = (transactionId, type) => {
        if (type === "Select") return;
        api.put(`/api/csv/transaction/${transactionId}`, { type })
            .then(() => {
                setTransactions(prev => prev.map(tx =>
                    tx.id === transactionId
                        ? { ...tx, classification: type, taxCategory: type === 'Personal' ? null : tx.taxCategory }
                        : tx
                ));
            })
            .catch(error => {
                console.error("Error saving transaction type:", error);
                alert("Failed to save transaction type. Please try again.");
            });
    };

    const handleSaveTaxCategory = (transactionId, taxCategory) => {
        api.put(`/api/csv/transaction/${transactionId}`, { taxCategory })
            .then(() => {
                setTransactions(prev => prev.map(tx =>
                    tx.id === transactionId ? { ...tx, taxCategory } : tx
                ));
            })
            .catch(error => {
                console.error("Error saving tax category:", error);
                alert("Failed to save tax category. Please try again.");
            });
    };

    const handleSavePersonalCategory = (transactionId, personalCategory) => {
        api.put(`/api/csv/transaction/${transactionId}`, { personalCategory })
            .then(() => {
                setTransactions(prev => prev.map(tx =>
                    tx.id === transactionId ? { ...tx, personalCategory } : tx
                ));
            })
            .catch(error => {
                console.error("Error saving personal category:", error);
                alert("Failed to save personal category. Please try again.");
            });
    };

    const handleAiCategorized = (results) => {
        setTransactions(prev => prev.map(tx => {
            const code = results[tx.id];
            if (!code) return tx;
            if (code.startsWith('h')) return { ...tx, personalCategory: code, taxCategory: null };
            return { ...tx, taxCategory: code, personalCategory: null };
        }));
    };

    const handleSubmit = () => {
        if (!startDate || !endDate) {
            alert("Please select both start and end dates");
            return;
        }

        setLoading(true);

        const formattedStartDate = startDate.toISOString().split('T')[0];
        const formattedEndDate = endDate.toISOString().split('T')[0];

        api.get(`/api/csv/expenses`, {
            params: {
                startDate: formattedStartDate,
                endDate: formattedEndDate,
            }
        })
            .then(response => {
                setTransactions(response.data);
                setHiddenTransactions(new Set());
            })
            .catch(error => {
                console.error("Error fetching transactions:", error);
                alert("Failed to fetch transactions. Please try again.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleSaveSummary = () => {
        const month = startDate ? startDate.toLocaleString('default', { month: 'long', year: 'numeric' }) : 'Unknown';
        const year = startDate ? startDate.getFullYear() : new Date().getFullYear();

        const summaryData = {
            totalDeposits: parseFloat(totalDeposits || 0),
            totalExpenses: parseFloat(totalExpenses || 0),
            profitOrLoss: parseFloat(profitOrLoss || 0),
            isProfit,
            month,
            year,
        };

        api.post(`/api/csv/saveSummary`, summaryData)
            .then(response => {
                alert('Summary data saved successfully!');
            })
            .catch(error => {
                console.error('Error saving summary data:', error);
                alert('Failed to save the summary data. Please try again.');
            });
    };

    const hasPayrollKeyword = (desc) => {
        const d = (desc || '').replace(/\s+/g, ' ').toLowerCase();
        const collapsed = d.replace(/\s/g, '');
        if (/payroll|paycheck|last pay\b|pay discrepancy|off-cycle|final pay/i.test(d)) return true;
        if (/payment\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i.test(d)) return true;
        if (/payroll|paycheck|lastpay|paydiscrepancy|offcycle|finalpay/i.test(collapsed)) return true;
        return false;
    };

    const normalizeVendor = (name, description) => {
        if (!name) return "Unknown Vendor";
        const upper = name.toUpperCase().replace(/[,.\s]+/g, ' ').trim();
        if (upper === 'GUSTO' || upper === 'GUSTO INC') {
            const desc = (description || '').toUpperCase();
            if (desc.includes('DES:FEE')) return 'Gusto Fees';
            return 'Gusto';
        }
        if (hasPayrollKeyword(description)) return 'Other Payroll';
        return name;
    };

    const groupTransactionsByVendor = (transactions) => {
        return transactions.reduce((acc, transaction) => {
            const vendor = normalizeVendor(transaction.tag?.tag, transaction.description);
            acc[vendor] = acc[vendor] || [];
            acc[vendor].push(transaction);
            return acc;
        }, {});
    };

    const calculateTotalAmount = (group) => {
        return group
            .filter(transaction => !hiddenTransactions.has(transaction.id))
            .reduce((sum, transaction) => sum + Math.abs(transaction.amount), 0)
            .toFixed(2);
    };

    const handleCheckboxChange = (transactionId) => {
        setHiddenTransactions(prev => {
            const newHiddenTransactions = new Set(prev);
            if (newHiddenTransactions.has(transactionId)) {
                newHiddenTransactions.delete(transactionId);
            } else {
                newHiddenTransactions.add(transactionId);
            }
            return newHiddenTransactions;
        });
    };

    const handleHideAllChange = (vendorTransactions, checked) => {
        setHiddenTransactions(prev => {
            const newHiddenTransactions = new Set(prev);
            vendorTransactions.forEach(transaction => {
                if (checked) {
                    newHiddenTransactions.add(transaction.id);
                } else {
                    newHiddenTransactions.delete(transaction.id);
                }
            });
            return newHiddenTransactions;
        });
    };

    const handleAccordionChange = (vendor) => {
        setExpandedGroup(prev => (prev === vendor ? null : vendor));
    };

    const getTransactionType = (tx) => tx.classification || tx.tag?.type || '';
    const isPersonal = (tx) => getTransactionType(tx).toLowerCase() === 'personal';
    const businessTransactions = transactions.filter(tx => !isPersonal(tx));
    const visibleTransactions = showAllTransactions ? transactions : businessTransactions;

    const deposits = groupTransactionsByVendor(visibleTransactions.filter(transaction => transaction.type.toLowerCase() === 'deposit'));
    const expenses = groupTransactionsByVendor(visibleTransactions.filter(transaction => transaction.type.toLowerCase() === 'expense'));

    const businessDeposits = groupTransactionsByVendor(businessTransactions.filter(tx => tx.type.toLowerCase() === 'deposit'));
    const businessExpenses = groupTransactionsByVendor(businessTransactions.filter(tx => tx.type.toLowerCase() === 'expense'));
    const totalDeposits = parseFloat(calculateTotalAmount(Object.values(businessDeposits).flat()));
    const totalExpenses = parseFloat(calculateTotalAmount(Object.values(businessExpenses).flat()));
    const profitOrLoss = (totalDeposits - totalExpenses).toFixed(2);
    const isProfit = profitOrLoss >= 0;

    const StatCard = ({ title, value, icon, color, gradient }) => (
        <Grow in timeout={600}>
            <Card
                sx={{
                    background: gradient,
                    color: 'white',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)'
                    }
                }}
            >
                <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Box>
                            <Typography variant="body2" sx={{ opacity: 0.9, mb: 1, fontSize: '0.875rem' }}>
                                {title}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                                ${value}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                borderRadius: '12px',
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {icon}
                        </Box>
                    </Box>
                </CardContent>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -20,
                        right: -20,
                        opacity: 0.1,
                        fontSize: '120px'
                    }}
                >
                    {icon}
                </Box>
            </Card>
        </Grow>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ 
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                py: 4
            }}>
                <Container maxWidth="xl">
                    {/* Hero Section */}
                    <Fade in timeout={800}>
                        <Paper
                            elevation={0}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                borderRadius: 4,
                                p: 4,
                                mb: 4,
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <Box position="relative" zIndex={1}>
                                <Box display="flex" alignItems="center" mb={1}>
                                    <Assessment sx={{ fontSize: 40, mr: 2 }} />
                                    <Typography variant="h3" fontWeight={700}>
                                        Financial Dashboard
                                    </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                    Monitor your business performance in real-time
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -50,
                                    right: -50,
                                    width: 300,
                                    height: 300,
                                    borderRadius: '50%',
                                    background: 'rgba(255,255,255,0.1)',
                                    filter: 'blur(60px)'
                                }}
                            />
                        </Paper>
                    </Fade>

                    <TransactionSearch year={startDate ? startDate.getFullYear() : new Date().getFullYear() - 1} />

                    {/* Date Picker Section */}
                    <Fade in timeout={1000}>
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                p: 3,
                                mb: 4,
                                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                            }}
                        >
                            <Box display="flex" alignItems="center" mb={2}>
                                <CalendarMonth sx={{ mr: 1, color: '#667eea' }} />
                                <Typography variant="h6" fontWeight={600}>
                                    Select Date Range
                                </Typography>
                            </Box>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={4}>
                                    <DatePicker
                                        label="Start Date"
                                        value={startDate}
                                        onChange={setStartDate}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                variant: 'outlined'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <DatePicker
                                        label="End Date"
                                        value={endDate}
                                        onChange={setEndDate}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                variant: 'outlined'
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        size="large"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                        sx={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            height: '56px',
                                            fontWeight: 600,
                                            boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                            '&:hover': {
                                                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
                                            }
                                        }}
                                    >
                                        {loading ? <CircularProgress size={24} color="inherit" /> : 'Get Transactions'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Fade>

                    {/* Statistics Cards */}
                    {transactions.length > 0 && (
                        <>
                            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showAllTransactions}
                                            onChange={(e) => setShowAllTransactions(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Show all transactions (including Personal)"
                                />
                            </Box>
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid item xs={12} md={4}>
                                    <StatCard
                                        title="Total Deposits"
                                        value={totalDeposits.toFixed(2)}
                                        icon={<AccountBalanceWallet sx={{ fontSize: 40 }} />}
                                        gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <StatCard
                                        title="Total Expenses"
                                        value={totalExpenses.toFixed(2)}
                                        icon={<Receipt sx={{ fontSize: 40 }} />}
                                        gradient="linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <StatCard
                                        title={isProfit ? "Net Profit" : "Net Loss"}
                                        value={Math.abs(profitOrLoss)}
                                        icon={isProfit ? 
                                            <TrendingUp sx={{ fontSize: 40 }} /> : 
                                            <TrendingDown sx={{ fontSize: 40 }} />
                                        }
                                        gradient={isProfit ?
                                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" :
                                            "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                                        }
                                    />
                                </Grid>
                            </Grid>

                            {/* Save Summary Button */}
                            <Box display="flex" justifyContent="flex-end" mb={4}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={handleSaveSummary}
                                    startIcon={<Assessment />}
                                    sx={{
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        px: 4,
                                        py: 1.5,
                                        fontWeight: 600,
                                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                            boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)'
                                        }
                                    }}
                                >
                                    Save Summary
                                </Button>
                            </Box>

                            {/* Transactions Section */}
                            <Fade in timeout={1200}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        p: 3,
                                        mb: 3,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                                    }}
                                >
                                    <Box display="flex" alignItems="center" mb={3}>
                                        <TrendingUp sx={{ mr: 1, color: '#11998e', fontSize: 28 }} />
                                        <Typography variant="h5" fontWeight={600}>
                                            Deposits
                                        </Typography>
                                        <Chip
                                            label={Object.keys(deposits).length}
                                            size="small"
                                            sx={{
                                                ml: 2,
                                                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                    {Object.entries(deposits).map(([vendor, vendorTransactions]) => (
                                        <TransactionGroup
                                            key={vendor}
                                            transactions={vendorTransactions}
                                            hiddenTransactions={hiddenTransactions}
                                            onHideAllChange={handleHideAllChange}
                                            onCheckboxChange={handleCheckboxChange}
                                            vendor={vendor}
                                            expanded={expandedGroup === vendor}
                                            onAccordionChange={() => handleAccordionChange(vendor)}
                                            onSaveTransactionType={handleSaveTransactionType}
                                            onSaveTaxCategory={handleSaveTaxCategory}
                                            onSavePersonalCategory={handleSavePersonalCategory}
                                            onTransactionsUpdated={handleAiCategorized}
                                        />
                                    ))}
                                </Paper>
                            </Fade>

                            <Fade in timeout={1400}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        borderRadius: 3,
                                        p: 3,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                                    }}
                                >
                                    <Box display="flex" alignItems="center" mb={3}>
                                        <TrendingDown sx={{ mr: 1, color: '#ee0979', fontSize: 28 }} />
                                        <Typography variant="h5" fontWeight={600}>
                                            Expenses
                                        </Typography>
                                        <Chip
                                            label={Object.keys(expenses).length}
                                            size="small"
                                            sx={{
                                                ml: 2,
                                                background: 'linear-gradient(135deg, #ee0979 0%, #ff6a00 100%)',
                                                color: 'white',
                                                fontWeight: 600
                                            }}
                                        />
                                    </Box>
                                    {Object.entries(expenses).map(([vendor, vendorTransactions]) => (
                                        <TransactionGroup
                                            key={vendor}
                                            transactions={vendorTransactions}
                                            hiddenTransactions={hiddenTransactions}
                                            onHideAllChange={handleHideAllChange}
                                            onCheckboxChange={handleCheckboxChange}
                                            vendor={vendor}
                                            expanded={expandedGroup === vendor}
                                            onAccordionChange={() => handleAccordionChange(vendor)}
                                            onSaveTransactionType={handleSaveTransactionType}
                                            onSaveTaxCategory={handleSaveTaxCategory}
                                            onSavePersonalCategory={handleSavePersonalCategory}
                                            onTransactionsUpdated={handleAiCategorized}
                                        />
                                    ))}
                                </Paper>
                            </Fade>
                        </>
                    )}

                    {/* Empty State */}
                    {!loading && transactions.length === 0 && (
                        <Fade in timeout={800}>
                            <Paper
                                elevation={0}
                                sx={{
                                    borderRadius: 3,
                                    p: 6,
                                    textAlign: 'center',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
                                }}
                            >
                                <Assessment sx={{ fontSize: 80, color: '#667eea', opacity: 0.5, mb: 2 }} />
                                <Typography variant="h5" color="text.secondary" gutterBottom>
                                    No Transactions Yet
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    Select a date range and click "Get Transactions" to view your financial data
                                </Typography>
                            </Paper>
                        </Fade>
                    )}
                </Container>
            </Box>
        </LocalizationProvider>
    );
}

export default Home;


